import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { AdministrationLayout, Badge, Cluster, EmptyState, Grid, Inline, RowActions, Skeleton, Surface, ToolbarActions } from '../components/ui';
import { useAuth } from '../lib/auth';
import { useCapabilities } from '../lib/capabilities';
import { useDiscovery } from '../lib/discovery';
import { listOrgFeatures, setOrgFeature, orgFeaturesKeys, type OrgFeatureView } from '../lib/api/orgFeatures.gen';

// Settings→Features (story 2-10, P02 §6, W3 §3): the ORG superadmin flips their
// OWN entitled flags on/off — org plane, deliberately BINARY (the locked/teaser
// state is an owner/platform concept, 2-9). Platform-managed rows (owner-set
// override or killSwitch) render padlocked (W1: see but not remove); plan-locked
// features sit at the bottom in teaser state and drive the 2-2 request flow.

type ApiError = { response?: { data?: { message?: string; reason?: string } }; message?: string };
const messageOf = (e: unknown): string => {
  const err = e as ApiError;
  return err.response?.data?.message || err.message || 'Something went wrong.';
};

export function OrgFeaturesPage(): JSX.Element {
  const { user } = useAuth();
  const { refetch: refetchCapabilities } = useCapabilities();
  const { openTeaser } = useDiscovery();
  const queryClient = useQueryClient();
  const orgId = user?.organizationId ?? '';
  const [error, setError] = useState<string | null>(null);

  const featuresQuery = useQuery({
    queryKey: orgFeaturesKeys.list({ orgId }),
    queryFn: () => listOrgFeatures(orgId).then((r) => r.data),
    enabled: Boolean(orgId),
  });

  const toggle = useMutation({
    mutationFn: (vars: { featureKey: string; enabled: boolean }) => setOrgFeature(orgId, vars),
    onSuccess: async () => {
      setError(null);
      await Promise.all([queryClient.invalidateQueries({ queryKey: orgFeaturesKeys.all }), refetchCapabilities()]);
    },
    onError: (e) => setError(messageOf(e)),
  });

  const features = useMemo(() => featuresQuery.data ?? [], [featuresQuery.data]);
  const { entitledByCategory, locked } = useMemo(() => {
    const entitled = features.filter((f) => f.entitled);
    const groups = new Map<string, OrgFeatureView[]>();
    entitled.forEach((f) => {
      if (!groups.has(f.category)) groups.set(f.category, []);
      groups.get(f.category)?.push(f);
    });
    return {
      entitledByCategory: [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)),
      // Plan-locked upsells (hidden-discovery rows are already omitted server-side).
      locked: features.filter((f) => !f.entitled),
    };
  }, [features]);

  const setState = (feature: OrgFeatureView, enabled: boolean) => {
    if (feature.platformManaged || (feature.state === 'on') === enabled) return;
    toggle.mutate({ featureKey: feature.key, enabled });
  };

  return (
    <AdministrationLayout
      header={{
        title: 'Features',
        description: 'Turn the features your plan includes on or off for this organization. Locked features show what an upgrade adds.',
      }}
    >
      {error && <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}

      {featuresQuery.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : features.length === 0 ? (
        <EmptyState title="No features" description="The feature catalog is empty for this organization." />
      ) : (
        <>
          {entitledByCategory.map(([category, list]) => (
            <Surface key={category} as="section" variant="flat" padding="lg" className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{category}</p>
              <Grid columns={{ base: 1, sm: 2 }} gap={2}>
                {list.map((feature) => (
                  <Cluster
                    key={feature.key}
                    // 2-146e finding: below `sm` this row is a single card in
                    // a one-column grid, but the row itself had no
                    // `flex-wrap` and its right-hand cluster (the BETA badge
                    // + On/Off button group) had no `shrink-0` — so at 390px
                    // the browser shrank the LEFT title/key column (its
                    // `min-w-0`) as far as it would go and the RIGHT
                    // cluster's own minimum content width still didn't fit,
                    // clipping "Off" past the card's own right edge
                    // (confirmed live: rendered as "Of", the final letter
                    // gone). `flex-wrap` + `shrink-0` on the controls lets
                    // the row drop the controls to their own line instead of
                    // clipping a control's own label.
                    justify="between"
                    gapX={3}
                    gapY={1}
                    className="rounded-lg border border-border/60 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{feature.name}</p>
                      <p className="truncate font-mono text-label text-muted-foreground">{feature.key}</p>
                    </div>
                    <Cluster gap={2} className="shrink-0">
                      {feature.status === 'beta' && <Badge tone="warning">beta</Badge>}
                      {feature.platformManaged ? (
                        <Inline
                          as="span"
                          gap={1}
                          className="text-xs font-medium text-muted-foreground"
                          title="Set by the platform owner — contact DefectSnap to change it."
                        >
                          <LockClosedIcon className="h-4 w-4" aria-hidden />
                          Platform-managed
                        </Inline>
                      ) : (
                        // Story 2-147xd: `ToolbarActions` (role/aria-label +
                        // `pressed` both grown onto the host vocabulary for
                        // exactly this repeated toggle-group shape — see
                        // `ActionDescriptor.pressed`'s own doc comment,
                        // ActionHosts.tsx).
                        <ToolbarActions
                          size="sm"
                          maxVisible={2}
                          role="group"
                          aria-label={`${feature.name} state`}
                          actions={([
                            { value: true, label: 'On' },
                            { value: false, label: 'Off' },
                          ] as const).map((option) => ({
                            id: option.label,
                            label: option.label,
                            variant: (feature.state === 'on') === option.value ? 'primary' : 'ghost',
                            pressed: (feature.state === 'on') === option.value,
                            disabled: toggle.isPending,
                            onClick: () => setState(feature, option.value),
                          }))}
                        />
                      )}
                    </Cluster>
                  </Cluster>
                ))}
              </Grid>
            </Surface>
          ))}

          {locked.length > 0 && (
            <Surface as="section" variant="flat" padding="lg" className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Locked on your plan
              </p>
              <Grid columns={{ base: 1, sm: 2 }} gap={2}>
                {locked.map((feature) => (
                  <Cluster
                    justify="between"
                    gap={3}
                    key={feature.key}
                    className="rounded-lg border border-dashed border-border/60 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <Cluster gap={1.5} className="truncate text-sm font-medium text-muted-foreground">
                        <LockClosedIcon className="h-4 w-4 shrink-0" aria-hidden />
                        {feature.name}
                      </Cluster>
                      <p className="truncate font-mono text-label text-muted-foreground">{feature.key}</p>
                    </div>
                    <RowActions
                      actions={[
                        {
                          id: 'learn-more',
                          label: 'Learn more',
                          variant: 'secondary',
                          onClick: () => openTeaser(feature.key, { name: feature.name }),
                        },
                      ]}
                    />
                  </Cluster>
                ))}
              </Grid>
            </Surface>
          )}
        </>
      )}
    </AdministrationLayout>
  );
}
